import { Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import AlertArchive from '../../../../shared/components/alert-archive/alert-archive';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import { usePagination } from '../../../../shared/hooks/use-pagination';
import { ScanRequestTableModel } from '../../../../shared/models/scan-request-table-model';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import { useAppSelector } from '../../../../store/hooks';
import { useGetRequestQuery } from '../../../request/api/request-api';
import RequestPreviewSider from '../../../request/components/request-preview-sider/request-preview-sider';
import { selectActiveRequestId, setActiveRequestId } from '../../../request/store/requests-slice';
import SignRequestCreateModal from '../../../sign-requests/components/sign-request-create-modal/sign-request-create-modal';
import SignRequestInPortalModal from '../../../sign-requests/components/sign-request-in-portal-modal/sign-request-in-portal-modal';
import { UserSettings } from '../../../user/models/user-settings';
import { useGetScanRequestsQuery } from '../../api/scan-requests-api';
import {
  selectActiveScanRequestId,
  selectIsScanRequestPreviewOpen,
  setActiveScanRequestId,
  setIsScanRequestPreviewOpen,
  setSelectedScanRequestIds,
} from '../../store/scan-requests-slice';
import ScanRequestArchiveModal from '../scan-request-archive-modal/scan-request-archive-modal';
import ScanRequestCreateFormModal from '../scan-request-create-form-modal/scan-request-create-form-modal';
import ScanRequestsHeader from '../scan-requests-header/scan-requests-header';
import ScanRequestsList from '../scan-requests-list/scan-requests-list';
import ScanRequestsTable from '../scan-requests-table/scan-requests-table';
import styles from './scan-requests-content.module.css';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { mapDateSearchParams } from '../../../sign-requests/utils/map-date-search-params';

const ScanRequestsContent = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = useBreakpoint();
  const activeRequestId = useAppSelector(selectActiveRequestId);
  const activeScanRequestId = useAppSelector(selectActiveScanRequestId);
  const isScanRequestPreviewOpen = useAppSelector(selectIsScanRequestPreviewOpen);
  const dispatch = useDispatch();
  const { data: userSettings } = useGetUserSettingsQuery();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { currentData: request, isFetching: isFetchingRequest } = useGetRequestQuery(activeRequestId!, {
    skip: !activeRequestId,
  });

  const [pageSize, setPageSize] = useState<number>();

  const { data: scanRequests, isFetching: isFetchingScanRequests } = useGetScanRequestsQuery(
    {
      page: Number(searchParams.get('page')) || 1,
      name: searchParams.get('name') || '',
      clientUser: searchParams.get('user') || '',
      unit: searchParams.get('unit') || '',
      customer: searchParams.get('customer') || '',
      ...(searchParams.has('archived') && {
        archived: searchParams.get('archived') === '1' ? '1' : '0',
      }),
      ...(searchParams.has('archived') && {
        canArchive: '1',
      }),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      limit: pageSize!,
      ...mapDateSearchParams((searchParams.get('dateFromTo') || '').toString()),
    },
    {
      skip: !pageSize,
    },
  );

  const { data: unArchivedScanRequests } = useGetScanRequestsQuery(
    {
      page: Number(searchParams.get('page')) || 1,
      name: searchParams.get('name') || '',
      clientUser: searchParams.get('user') || '',
      unit: searchParams.get('unit') || '',
      customer: searchParams.get('customer') || '',
      archived: '0',
      canArchive: '1',
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      limit: pageSize!,
      ...mapDateSearchParams((searchParams.get('dateFromTo') || '').toString()),
    },
    {
      skip: !pageSize,
    },
  );

  const totalRequests = useMemo(
    () =>
      pageSize && (scanRequests?.items || []).length < pageSize
        ? pageSize * (scanRequests?.page || 1)
        : scanRequests?.total,
    [pageSize, scanRequests],
  );

  const [limit, pagination] = usePagination(totalRequests, UserSettings.ScanRequestsPageSize);

  const scanRequestsDataSource: ScanRequestTableModel[] = useMemo(() => {
    return (scanRequests?.items || []).map(scanRequest => {
      const requestDataChildren: ScanRequestTableModel[] =
        scanRequest.request.id === request?.request.id
          ? (request?.scanRequests || [])
              .filter(requestScanRequest => requestScanRequest.id !== scanRequest.id)
              .map(requestScanRequest => ({
                id: requestScanRequest.id,
                key: `${scanRequest.id}_${requestScanRequest.id}`,
                name: requestScanRequest.name,
                user: requestScanRequest.clientUser?.name || '',
                customer: `${requestScanRequest.customer?.name || ''} ${requestScanRequest.customer?.surname || ''}`,
                unit: requestScanRequest.unit || '',
                status: requestScanRequest.status,
                request: scanRequest.request,
                scannedAt: convertServerDateTimeString(
                  requestScanRequest.scannedAt,
                  userSettings?.[UserSettings.DateFormat],
                ),
                signRequestCount: requestScanRequest.request.signCount || 0,
                scanRequestCount: requestScanRequest.request.scanCount || 0,
                isDeleted: requestScanRequest.deleted,
                canArchive: requestScanRequest.canArchive,
                archived: requestScanRequest.archived,
              }))
          : [];

      return {
        id: scanRequest.id,
        key: scanRequest.id,
        name: scanRequest.name,
        user: scanRequest.clientUser?.name || '',
        customer: `${scanRequest.customer?.name || ''} ${scanRequest.customer?.surname || ''}`,
        unit: scanRequest.unit || '',
        status: scanRequest.status,
        request: scanRequest.request,
        scannedAt: convertServerDateTimeString(scanRequest.scannedAt, userSettings?.[UserSettings.DateFormat]),
        signRequestCount: scanRequest.request.signCount || 0,
        scanRequestCount: scanRequest.request.scanCount || 0,
        isDeleted: scanRequest.deleted,
        canArchive: scanRequest.canArchive,
        archived: scanRequest.archived,
        children:
          isFetchingRequest && scanRequest.request.scanCount > 1
            ? [
                {
                  key: `${scanRequest.id}_spinner`,
                  name: <Spin />,
                },
              ]
            : requestDataChildren,
      };
    });
  }, [scanRequests, request, isFetchingRequest, userSettings]);

  useEffect(() => {
    setPageSize(limit);
  }, [limit]);

  const handleFilteringUnarchived = () => {
    setSearchParams(existingSearchParams => {
      existingSearchParams.set('archived', '0');
      existingSearchParams.set('canArchive', '1');
      return existingSearchParams;
    });
    dispatch(setSelectedScanRequestIds((unArchivedScanRequests?.items || []).map(scanRequest => scanRequest.id)));
  };

  return (
    <>
      {unArchivedScanRequests?.items && unArchivedScanRequests.items.length > 0 && (
        <AlertArchive onAction={handleFilteringUnarchived} />
      )}
      {scanRequests && (
        <div className={styles.scanRequests}>
          <ScanRequestsHeader pagination={pagination} unarchivedScanRequests={unArchivedScanRequests?.items || []} />
          {screens.sm ? (
            <div className={styles.contentContainer}>
              <div className={styles.tableContainer}>
                <ScanRequestsTable
                  isFetchingScanRequests={isFetchingScanRequests}
                  scanRequestsDataSource={scanRequestsDataSource}
                />
              </div>
              <RequestPreviewSider
                collapsed={!activeScanRequestId || !isScanRequestPreviewOpen}
                onClose={() => {
                  dispatch(setActiveRequestId(null));
                  dispatch(setActiveScanRequestId(null));
                  dispatch(setIsScanRequestPreviewOpen(false));
                }}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                fullscreenUrl={Routes.ScanRequest.replace(':scanRequestId', activeScanRequestId!).replace(
                  ':requestId',
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  activeRequestId!,
                )}
              />
            </div>
          ) : (
            <ScanRequestsList
              isFetchingScanRequests={isFetchingScanRequests}
              scanRequestsDataSource={scanRequestsDataSource}
            />
          )}
          <ScanRequestCreateFormModal />
        </div>
      )}
      <SignRequestCreateModal />
      <ScanRequestArchiveModal />
      <SignRequestInPortalModal />
    </>
  );
};
export default ScanRequestsContent;
